<script setup>
import { onMounted, computed, ref, readonly, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useForm } from 'vee-validate';
import { cloneDeep } from 'lodash';
import { format, parse, isDate } from 'date-fns';

import InputTextGroup from '@/components/forms/groups/InputTextGroup.vue';
import DropdownGroup from '@/components/forms/groups/DropdownGroup.vue';
import CheckboxGroup from '@/components/forms/groups/CheckboxGroup.vue';
import DSButton from 'primevue/button';
import FormPhoneInput from '@/components/forms/FormPhoneInput.vue';
import CalendarGroup from '@/components/forms/groups/CalendarGroup.vue';
import UploadFileWithDownloadableFileList from '@/components/upload/UploadFileWithDownloadableFileList.vue';
import UserAddressFields from '@/components/forms/UserAddressFields.vue';
import {
  getAllCallingCodes,
  getPhoneNumberCountryCode,
  getPhoneNumberWithoutCountryCode,
} from '@/utils/phone-numbers';
import {
  yup,
  validators,
  citizenshipIsTaxIdCountry,
} from '@clearbanc/validation';
import { RouteNames } from '@/data/routes';
import { objectDiff } from '@/utils/object-comparison';
import { getDisplayedErrorMessage } from '@/data/error-messages';
import { JobTitlesByCorpCountry } from '@/data/owner';
import { COUNTRY_CODES } from '@/data/supported-country-codes';
import {
  OWNER_ROLES,
  ENTITY_TYPE,
  ID_TYPE,
} from '@clearbanc/data-common-types';
import { personalTaxIdLabelShort } from '@/utils/local';
import { countryOptionsWithPriority } from '@/data/country-code-with-names';
import analytics, {
  SEGMENT_EVENTS,
  trackBusinessProfileStepCompletion,
} from '@/utils/analytics';
import { useToast } from '@/composables/useToast';
import { useProfiling } from '@/composables/useProfiling';
import { useDocumentUploads } from '@/composables/useDocumentUploads';

const MAX_TOTAL_FILE_SIZE_MB = 50;

// -----
// Setup
// -----

const store = useStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const { areDocumentUploadsRequired, areDocumentUploadsHidden } =
  useDocumentUploads();
const props = defineProps({
  selectedOwner: {
    type: Object,
  },
  businessCorpCountry: {
    type: String,
  },
  removeContainer: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['saveSuccess']);
const profiling = useProfiling(route.name, {
  inviteId: props.selectedOwner?.uuid,
  type: store.getters.eventTypeBasedOnLoginsCount,
});

// ---------
// Constants
// ---------

const COUNTRY_CALLING_CODES = getAllCallingCodes();
const COUNTRIES = countryOptionsWithPriority([
  COUNTRY_CODES.US,
  COUNTRY_CODES.CA,
  COUNTRY_CODES.GB,
]);

// ---------
// Variables
// ---------

const isPrimaryUser = computed(() => !!props.selectedOwner?.isPrimary);
const errorMessage = ref(null);
const requestStatuses = readonly({
  updateUser: computed(() => store.getters.requestStatus('UPDATE_USER')),
  updateUserWithoutAuth: computed(() =>
    store.getters.requestStatus('UPDATE_USER_WITHOUT_AUTH'),
  ),
});
const formRefUserAddress = ref();

// -------
// Methods
// -------

function onBack() {
  router.push({
    name: RouteNames.OWNERS.INDEX,
  });
}

// -------
// Uploads
// -------

function generateFileFilter(...args) {
  return {
    metaId: props.selectedOwner?.id,
    entity: isPrimaryUser.value ? ENTITY_TYPE.USER : ENTITY_TYPE.OWNER,
    type: args.length > 1 ? args : args[0],
  };
}

function removeUpload(id) {
  store.dispatchApiAction('DELETE_USER_UPLOAD', { id });
}

const userUploads = computed(() => {
  const filter = generateFileFilter(
    ID_TYPE.PASSPORT_OR_LICENSE,
    ID_TYPE.ID,
    ID_TYPE.PASSPORT,
    ID_TYPE.DRIVERS_LICENSE,
  );

  return store.getters.uploadsMatching(filter);
});

// ----------
// Form Logic
// ----------

const validationSchema = computed(() =>
  yup.object({
    firstName: yup.userName(),
    lastName: yup.userName(),
    email: yup.email(),
    countryCallingCode: yup.phoneCode(),
    phone: yup.phone('countryCallingCode'),
    jobTitle: yup.jobTitle(),
    citizenship: yup.citizenship(),
    personalTaxId: props.selectedOwner?.personalTaxId
      ? yup
          .string()
          .validator('ssn-length', 'citizenship', validators.validateSSN)
      : yup
          .string()
          .requiredWhen(
            'citizenship',
            (country) => country && citizenshipIsTaxIdCountry(country),
            'Required',
          )
          .validator('ssn-length', 'citizenship', validators.validateSSN),
    passportNumber: yup.passport('citizenship'),
    birthday: yup.birthday(),
    documentUploads: yup
      .string()
      .nullable()
      .when([], {
        is: () => {
          if (!areDocumentUploadsRequired.value) {
            return false;
          }

          return userUploads.value.length < 1;
        },
        then: (schema) =>
          schema.required('Please upload your passport or drivers license.'),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        'files-size-limit',
        `The total size of selected files cannot exceed the allowed limit of ${MAX_TOTAL_FILE_SIZE_MB} MB`,
        () => {
          return true;
        },
      ),
  }),
);

const { values, handleSubmit, setValues, isSubmitting, errors, validateField } =
  useForm({
    validationSchema,
  });

function populateSavedValues() {
  const initialValues = {};

  initialValues.firstName = props.selectedOwner?.firstName;
  initialValues.lastName = props.selectedOwner?.lastName;
  initialValues.phone = getPhoneNumberWithoutCountryCode(
    props.selectedOwner?.phoneNumber,
  );
  initialValues.countryCallingCode =
    getPhoneNumberCountryCode(props.selectedOwner?.phoneNumber) ||
    COUNTRY_CALLING_CODES.find((item) => {
      return item.value === COUNTRY_CODES.US;
    });
  initialValues.email = props.selectedOwner?.email;
  initialValues.passportNumber = props.selectedOwner?.passportNumber;
  initialValues.birthday = props.selectedOwner?.birthday;

  // Checkboxes
  if (props.selectedOwner?.jobRoles?.includes(OWNER_ROLES.DIRECTOR)) {
    initialValues.isDirector = [OWNER_ROLES.DIRECTOR];
  }

  if (props.selectedOwner?.jobRoles?.includes(OWNER_ROLES.OWNER)) {
    initialValues.isSignificantPercentageOwner = [OWNER_ROLES.OWNER];
  }

  // Job Title
  if (props.selectedOwner?.jobTitle) {
    initialValues.jobTitle = JobTitlesByCorpCountry(
      props.businessCorpCountry || COUNTRY_CODES.US,
    )?.find((item) => {
      return item.value === props.selectedOwner?.jobTitle;
    });
  }

  // Citizenship
  if (props.selectedOwner?.citizenship) {
    initialValues.citizenship = COUNTRIES?.find(
      (country) => country.value === props.selectedOwner?.citizenship,
    );
  }

  setValues(initialValues);
}

function formatFormDataForEndpoint(initialData, formValues, userAddressValues) {
  const updatedData = cloneDeep(initialData);

  updatedData.firstName = formValues.firstName;
  updatedData.lastName = formValues.lastName;
  updatedData.jobTitle = formValues.jobTitle.value;
  updatedData.citizenship = formValues.citizenship.value;

  if (userAddressValues?.address) {
    updatedData.address = {
      ...updatedData.address,
      ...userAddressValues.address,
    };
  }

  updatedData.jobRoles = [];

  if (formValues.isSignificantPercentageOwner?.length) {
    updatedData.jobRoles.push(OWNER_ROLES.OWNER);
  }

  if (formValues.isDirector?.length) {
    updatedData.jobRoles.push(OWNER_ROLES.DIRECTOR);
  }

  if (props.selectedOwner?.jobRoles?.includes(OWNER_ROLES.SIGNATORY)) {
    updatedData.jobRoles.push(OWNER_ROLES.SIGNATORY);
  }

  updatedData.phoneNumber =
    `${formValues.countryCallingCode.callingCode}${formValues.phone}`.replace(
      /\s+/g,
      '',
    );

  if (formValues.personalTaxId) {
    updatedData.personalTaxId = formValues.personalTaxId;
  }
  updatedData.passportNumber = formValues.passportNumber;

  const birthdayDate = isDate(formValues.birthday)
    ? formValues.birthday
    : parse(formValues.birthday, 'yyyy-MM-dd', new Date());
  updatedData.birthday = format(birthdayDate, 'yyyy-MM-dd');

  return updatedData;
}

async function submitForm(userAddressValues) {
  // Gather and format data for the endpoint
  const updatedSelectedOwner = formatFormDataForEndpoint(
    props.selectedOwner,
    values,
    userAddressValues,
  );

  // Compare the data to confirm there were changes made
  const { changedProps: payload } = objectDiff(
    updatedSelectedOwner,
    props.selectedOwner,
  );

  // Make the request
  if (Object.keys(payload)?.length) {
    await store.dispatchApiAction('UPDATE_USER', {
      ...payload,
      id: props.selectedOwner?.id,
    });

    if (requestStatuses.updateUser.error) {
      errorMessage.value = getDisplayedErrorMessage(
        requestStatuses.updateUser.error,
      );
      return;
    }
    toast.success(`Changes saved.`);
  }

  emit('saveSuccess');
  profiling.finish();

  if (!props.removeContainer) {
    router.push({
      name: RouteNames.OWNERS.INDEX,
    });
  }
}

const onSubmit = handleSubmit(async () => {
  errorMessage.value = null;

  const userAddressValues =
    await formRefUserAddress.value?.validateAndGetValues();

  if (!userAddressValues) {
    errorMessage.value = 'Please fix the validation errors.';
    return;
  }

  analytics.track(SEGMENT_EVENTS.if_owner_profile_completed, {
    completed_user_id: props.selectedOwner?.id,
    completed_user_email: props.selectedOwner?.email,
  });

  trackBusinessProfileStepCompletion(store.getters.businessProfileSteps);

  return submitForm(userAddressValues);
});

const onCitizenshipChange = () => {
  setValues({
    ...values,
    personalTaxId: null,
    passportNumber: null,
  });
};

// Vee Validate doesn't play well with our custom file upload component
// This is required to revalidate the form when a file is uploaded
watch([userUploads], () => {
  validateField('documentUploads');
});

// -----------------
// Lifecycle Methods
// -----------------

onMounted(() => {
  if (props.selectedOwner?.email) {
    populateSavedValues();
  }
});
</script>

<template>
  <div :class="props.removeContainer ? '' : $style.container">
    <form
      class="c-form"
      :style="{ pointerEvents: isSubmitting ? 'none' : 'all' }"
      @submit="onSubmit"
    >
      <!-- ////////// -->
      <!-- Checkboxes -->
      <!-- ////////// -->

      <div class="c-group">
        <div class="c-form__row">
          <CheckboxGroup
            name="isDirector"
            :options="[
              {
                value: OWNER_ROLES.DIRECTOR,
                label: 'This individual is a Director / Board member',
              },
            ]"
          />
          <CheckboxGroup
            name="isSignificantPercentageOwner"
            :options="[
              {
                value: OWNER_ROLES.OWNER,
                label: 'This individual has 25%+ ownership',
              },
            ]"
          />
        </div>
      </div>

      <!-- //////////////////// -->
      <!-- Personal Information -->
      <!-- //////////////////// -->

      <div class="c-group">
        <div class="c-group__header">
          <h3 class="c-group__header__title">Personal Information</h3>
        </div>

        <div class="c-form__row grid grid-cols-12">
          <div class="col-span-6 md:col-span-12">
            <InputTextGroup name="firstName" label="First Name" />
          </div>
          <div class="col-span-6 md:col-span-12">
            <InputTextGroup name="lastName" label="Last Name" />
          </div>
        </div>

        <div class="c-form__row grid grid-cols-12">
          <div class="col-span-6 md:col-span-12">
            <InputTextGroup disabled name="email" label="Email" />
          </div>
          <div class="col-span-6 md:col-span-12">
            <FormPhoneInput
              :dropdown-attrs="{
                name: 'countryCallingCode',
                options: COUNTRY_CALLING_CODES,
                optionLabel: 'nameString',
                placeholder: 'Select',
                filter: true,
              }"
              :input-attrs="{
                name: 'phone',
                label: 'Phone Number',
              }"
            />
          </div>
        </div>

        <div class="c-form__row grid grid-cols-12">
          <div class="col-span-6 md:col-span-12">
            <DropdownGroup
              name="jobTitle"
              :label="$t('common.jobTitle')"
              option-label="label"
              :options="JobTitlesByCorpCountry(props.businessCorpCountry)"
              :placeholder="$t('common.selectOption')"
              append-to="self"
            />
          </div>
          <div class="col-span-6 md:col-span-12">
            <DropdownGroup
              filter
              reset-filter-on-hide
              name="citizenship"
              label="Country of Citizenship"
              option-label="nameString"
              :options="COUNTRIES"
              :placeholder="$t('common.selectOption')"
              append-to="self"
              @change="onCitizenshipChange"
            />
          </div>
        </div>

        <div class="c-form__row grid grid-cols-12">
          <div class="col-span-6 md:col-span-12">
            <div
              v-if="
                values?.citizenship?.value &&
                citizenshipIsTaxIdCountry(values?.citizenship?.value)
              "
            >
              <InputTextGroup
                v-only-numbers
                name="personalTaxId"
                :placeholder="props.selectedOwner?.personalTaxId"
                :label="personalTaxIdLabelShort(values?.citizenship?.value)"
                :use-grouping="false"
              />
            </div>
            <div v-else>
              <InputTextGroup
                name="passportNumber"
                label="Passport Number"
                :use-grouping="false"
              />
            </div>
          </div>
          <div class="col-span-6 md:col-span-12">
            <CalendarGroup
              :label="$t('common.dateOfBirth')"
              name="birthday"
              :max-date="new Date()"
              :manual-input="false"
              placeholder="yyyy-mm-dd"
              show-icon
            />
          </div>
        </div>
      </div>

      <!-- /////// -->
      <!-- Address -->
      <!-- /////// -->

      <UserAddressFields :user="props.selectedOwner" ref="formRefUserAddress" />

      <!-- ///////// -->
      <!-- Documents -->
      <!-- ///////// -->

      <div class="c-group" v-if="!areDocumentUploadsHidden">
        <div class="c-group__header">
          <h3 class="c-group__header__title">Documents</h3>
          <p class="c-group__header__description">
            Upload any available documents to support your application.
          </p>
          <InputTextGroup
            name="documentUploads"
            label=""
            :style="{ display: 'none' }"
          />
        </div>
        <div class="c-form__row grid grid-cols-12">
          <div class="col-span-6 md:col-span-12">
            <p class="c-group__header__description mb-2">
              Passport (front and back)
            </p>

            <UploadFileWithDownloadableFileList
              ref="uploadFileWithDownloadableFileList"
              :upload-filters="generateFileFilter(ID_TYPE.PASSPORT)"
              :display-filters="generateFileFilter(ID_TYPE.PASSPORT)"
              :empty-prompt="
                $t('common.messagePrompt.dragAndDropFilesOrBrowse')
              "
              theme="onboarding"
              no-list
              :files="
                store.getters.uploadsMatching(
                  generateFileFilter(ID_TYPE.PASSPORT),
                )
              "
              @fileRemove="removeUpload"
            />
          </div>
          <div class="col-span-6 md:col-span-12">
            <p class="c-group__header__description mb-2">
              Driver’s license (front and back)
            </p>

            <UploadFileWithDownloadableFileList
              ref="uploadFileWithDownloadableFileList"
              :upload-filters="generateFileFilter(ID_TYPE.DRIVERS_LICENSE)"
              :display-filters="generateFileFilter(ID_TYPE.DRIVERS_LICENSE)"
              :empty-prompt="
                $t('common.messagePrompt.dragAndDropFilesOrBrowse')
              "
              theme="onboarding"
              no-list
              :files="
                store.getters.uploadsMatching(
                  generateFileFilter(ID_TYPE.DRIVERS_LICENSE),
                )
              "
              @fileRemove="removeUpload"
            />
          </div>
        </div>
      </div>

      <!-- //////// -->
      <!-- Controls -->
      <!-- //////// -->

      <div class="c-form__controls" :style="{ flexWrap: 'wrap' }">
        <DSButton
          v-if="!props.removeContainer"
          label="Back"
          class="p-button-outlined"
          :loading="isSubmitting"
          @click="onBack"
        />
        <DSButton label="Save Profile" type="submit" :loading="isSubmitting" />
        <div
          v-if="Object.keys(errors).length >= 1 || errorMessage"
          class="p-error"
          :class="$style['controls-error']"
        >
          <span v-if="errorMessage">
            {{ errorMessage }}
          </span>
          <span v-else>See errors above</span>
        </div>
      </div>
    </form>
  </div>
</template>

<style module>
.container {
  padding: 20px;
  margin-top: 40px;
  border: 1px solid #e8e8ea;
  box-shadow: 0 2px 8px 0 #0000000f;
  background: #fff;
}

.controls-error {
  text-align: right;
  width: 100%;
  margin-top: 20px;
}

@media (min-width: 800px) {
  .container {
    padding: 40px;
  }
}
</style>
